import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, ArrowLeft, Shield, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { tierId } = useParams();
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [existingSubscription, setExistingSubscription] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch tier and check for existing subscription in parallel
        const [tiersRes, subRes] = await Promise.all([
          axios.get(`${API}/tiers`),
          axios.get(`${API}/subscriptions/my`)
        ]);
        
        const selectedTier = tiersRes.data.find(t => t.id === tierId);
        if (selectedTier) {
          setTier(selectedTier);
        } else {
          toast.error('Tier not found');
          navigate('/pricing');
          return;
        }
        
        // Check if user already has an active or pending subscription
        if (subRes.data && ['ACTIVE', 'PENDING', 'PENDING_APPROVAL'].includes(subRes.data.status)) {
          setExistingSubscription(subRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load checkout details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [tierId, navigate]);

  const createSubscription = async (subscriptionId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscriptions`, {
        tier_id: tierId,
        paypal_subscription_id: subscriptionId
      });
      
      // Handle different subscription statuses
      if (response.data.status === 'PENDING_APPROVAL') {
        toast.success('Payment successful! Your membership is awaiting admin approval.');
      } else if (response.data.status === 'ACTIVE') {
        toast.success('Subscription activated! Welcome to the Royalty!');
      } else {
        toast.success('Payment received! Processing your membership...');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate subscription');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tier) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-gold-400" />
              <span className="font-heading text-xl font-bold text-white">House of Royalty</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to pricing
          </Link>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Order Summary */}
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-8">
                Complete Your Order
              </h1>

              <div className="glass-heavy p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-heading font-semibold text-white">{tier.name} Membership</h3>
                  <span className="badge badge-gold">{tier.is_popular ? 'Popular' : 'Selected'}</span>
                </div>
                <p className="text-zinc-500 text-sm mb-6">{tier.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Monthly subscription</span>
                    <span className="text-2xl font-heading font-bold gold-text">${tier.price}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 text-zinc-500 text-sm">
                <Shield className="w-5 h-5 text-gold-400" />
                <span>Secure checkout powered by PayPal</span>
              </div>
            </div>

            {/* Payment */}
            <div>
              <div className="glass-heavy p-8">
                <h2 className="text-xl font-heading font-semibold text-white mb-6">Payment Details</h2>
                
                <div className="mb-6 p-4 bg-black/30 border border-white/5">
                  <p className="text-zinc-400 text-sm">
                    Subscribing as: <span className="text-white">{user?.email}</span>
                  </p>
                </div>

                {existingSubscription ? (
                  <div className="text-center py-8 bg-amber-500/10 border border-amber-500/30 p-6">
                    <p className="text-amber-400 font-medium mb-2">
                      {existingSubscription.status === 'PENDING_APPROVAL' 
                        ? '⏳ Subscription Awaiting Approval' 
                        : '✅ You Already Have a Subscription'}
                    </p>
                    <p className="text-zinc-400 text-sm mb-4">
                      {existingSubscription.status === 'PENDING_APPROVAL'
                        ? 'Your payment was successful! Your membership is pending admin approval. You\'ll receive access once approved.'
                        : `You already have an active ${existingSubscription.tier_name} membership.`}
                    </p>
                    <Link to="/dashboard" className="btn-gold inline-block">
                      Go to Dashboard
                    </Link>
                  </div>
                ) : processing ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-zinc-400">Processing your subscription...</p>
                  </div>
                ) : !tier.paypal_plan_id ? (
                  <div className="text-center py-8 bg-amber-500/10 border border-amber-500/30 p-6">
                    <p className="text-amber-400 font-medium mb-2">⚠️ Payments Not Yet Configured</p>
                    <p className="text-zinc-400 text-sm">
                      This membership tier is not yet available for purchase. Please contact the administrator or try again later.
                    </p>
                  </div>
                ) : (
                  <div data-testid="paypal-buttons">
                    <PayPalButtons
                      style={{
                        shape: 'rect',
                        color: 'gold',
                        layout: 'vertical',
                        label: 'subscribe'
                      }}
                      createSubscription={(data, actions) => {
                        return actions.subscription.create({
                          plan_id: tier.paypal_plan_id,
                          application_context: {
                            shipping_preference: 'NO_SHIPPING'
                          }
                        });
                      }}
                      onApprove={async (data) => {
                        await createSubscription(data.subscriptionID);
                      }}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        toast.error('Payment failed. Please try again.');
                      }}
                      onCancel={() => {
                        toast.info('Payment cancelled');
                      }}
                    />
                  </div>
                )}

                <p className="text-zinc-600 text-xs text-center mt-6">
                  By subscribing, you agree to our terms of service and privacy policy.
                  You can cancel your subscription at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
